import { useT } from '@/i18n/runtime';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

/** How long "Good shot" stays (SPEC_CAPTURE_UX_R2 §5: ~700 ms, never blocking the retake / use-this pills). */
export const AFFIRMATION_MS = 700;

/** The trigger hook: `show` flips true for AFFIRMATION_MS after `trigger()`; a second trigger restarts the clock. */
export function useAffirmation(): { readonly show: boolean; readonly trigger: () => void } {
  const [show, setShow] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );
  const trigger = useCallback(() => {
    setShow(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setShow(false);
    }, AFFIRMATION_MS);
  }, []);
  return { show, trigger };
}

/**
 * The transient "Good shot" over the viewfinder — pointer-events none, so the pills beneath stay tappable at once.
 * Decorative for assistive tech: the persistent hint line under the card already announces the outcome, so this flash
 * is aria-hidden rather than a second live region saying the same thing.
 */
export function Affirmation({ show }: { show: boolean }): JSX.Element | null {
  const { t } = useT();
  if (!show) return null;
  return (
    <div className="sv-affirm" data-testid="affirmation" aria-hidden="true">
      <span className="sv-affirm-disc">
        <Icon name="check" size={22} />
      </span>
      <span>{t('sv.affirm.good')}</span>
    </div>
  );
}
